import { css } from "@emotion/css";
import type { CalloutType } from "@octopusdeploy/design-system-components";
import { Callout } from "@octopusdeploy/design-system-components";
import { borderRadius, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import cn from "classnames";
import { CodeEditor, Language } from "~/components/CodeEditor/CodeEditor";
interface RegoCodeEditorProps {
    value: string;
    onChange: (rego: string) => void;
    error: string | undefined;
    showPlaceholderAsError?: boolean;
    placeholders?: string[];
}
export function RegoCodeEditor({ value, onChange, error, showPlaceholderAsError, placeholders }: RegoCodeEditorProps) {
    const remainingPlaceholders = getRemainingPlaceholders(value, placeholders);
    const hasPlaceholders = remainingPlaceholders.length > 0;
    const hasError = (hasPlaceholders && showPlaceholderAsError) || error;
    return (<>
            {hasPlaceholders && <RegoPlaceholderMessage placeholders={remainingPlaceholders} type={showPlaceholderAsError ? "danger" : "warning"}/>}
            <div className={cn(hasError ? styles.error : null)}>
                <CodeEditor value={value} onChange={onChange} language={Language.Rego} showToolbar={true} showCopyButton={true} allowFullScreen={true}/>
            </div>
        </>);
}
function RegoPlaceholderMessage({ placeholders, type }: {
    placeholders: Array<{
        placeholder: string;
        line: number;
    }>;
    type: CalloutType;
}) {
    const description = buildPlaceholderDescription(placeholders);
    if (!description) {
        return null;
    }
    return (<div style={{ marginBottom: space[8] }}>
            <Callout type={type} title={"Details required"}>
                {description}
            </Callout>
        </div>);
}
function getRemainingPlaceholders(value: string, placeholders: string[] | undefined): Array<{
    placeholder: string;
    line: number;
}> {
    if (!placeholders)
        return [];
    const lines = value.split("\n");
    return placeholders
        .map((placeholder) => {
        const lineIndex = lines.findIndex((line) => line.includes(placeholder));
        return lineIndex >= 0 ? { placeholder, line: lineIndex + 1 } : null;
    })
        .filter((p): p is {
        placeholder: string;
        line: number;
    } => p !== null);
}
function buildPlaceholderDescription(placeholders: Array<{
    placeholder: string;
    line: number;
}>): string | null {
    if (placeholders.length === 0)
        return null;
    const parts = placeholders.map((p, i) => `${i === 0 ? "the " : ""}${p.placeholder} on line ${p.line}`);
    if (parts.length === 1) {
        return `Please specify ${parts[0]}.`;
    }
    const allButLast = parts.slice(0, -1).join(", ");
    return `Please specify ${allButLast}, and ${parts[parts.length - 1]}.`;
}
const styles = {
    error: css({
        border: `1px solid ${themeTokens.color.border.danger}`,
        borderRadius: borderRadius.medium,
        overflow: "clip",
    }),
};
